import { useVendorContext } from "@client-shared/providers/vendor";
import type { AccessibleModule, AccessiblePages, FeatureName, ModulePermissions, PageAction, PagePermission, Vendor } from "@shared/types";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface RBACContextType {
  /** The feature name for this vendor (BLP, SS, BC OG) */
  featureName: FeatureName | undefined;
  /** Raw permissions from API */
  permissions: AccessiblePages | undefined;
  /** Check if user has access to a specific page (e.g., "orders.view") */
  hasPageAccess: (page: string) => boolean;
  /** Check if user has access to a menu group (e.g., "orders") */
  hasModuleAccess: (module: AccessibleModule) => boolean;
  /** Check if user has a specific action on a module */
  hasAction: (module: AccessibleModule, action: PageAction) => boolean;
  /** Get all available actions for a module */
  getModuleActions: (module: AccessibleModule) => PageAction[];
  /** Loading state */
  isLoading: boolean;
  /** Set permissions (for testing/stories or when API returns) */
  clearState?: () => void;
  setPermissions: (permissions: AccessiblePages, featureName: FeatureName) => void;
}

export const RBACContext = createContext<RBACContextType | undefined>(undefined);

const isPagePermissionArray = (pages: Array<string | PagePermission>): pages is PagePermission[] =>
  pages.length > 0 && typeof pages[0] === "object";

export const buildModulePermissions = (permissions: AccessiblePages | undefined): ModulePermissions => {
  const pages = permissions?.pages ?? [];
  if (!pages.length) return {};

  // New object format: { page: "orders", actions: ["view", "create"] }
  if (isPagePermissionArray(pages)) {
    const modulePermissions: Record<string, PageAction[]> = {};

    for (const { page, actions } of pages) {
      modulePermissions[page] ??= [];
      for (const action of actions) {
        if (!modulePermissions[page].includes(action)) {
          modulePermissions[page].push(action);
        }
      }
    }

    return modulePermissions;
  }

  // Legacy string format: "orders.view"
  const permissionTokens = permissions?.actions?.length ? permissions.actions : pages;

  return (permissionTokens as string[]).reduce((acc, pageStr) => {
    const parts = pageStr.split(".");
    if (parts.length >= 2) {
      const [module, action] = parts as [AccessibleModule, PageAction];
      acc[module] ??= [];
      if (action && !acc[module].includes(action)) {
        acc[module].push(action);
      }
    }
    return acc;
  }, {} as ModulePermissions);
};

export const checkPageAccess = (permissions: AccessiblePages | undefined, page: string): boolean => {
  const pages = permissions?.pages;
  if (!pages?.length) return false;

  // New object format
  if (isPagePermissionArray(pages)) {
    // Direct match (e.g., page="orders" matches p.page="orders")
    if (pages.some((p) => p.page === page)) return true;

    // Legacy "page.action" format (e.g., "orders.view" → pageName="orders", action="view")
    // Also handles sub-pages (e.g., "finance.invoices.view" → pageName="finance.invoices", action="view")
    const lastDotIndex = page.lastIndexOf(".");
    if (lastDotIndex === -1) return false;

    const pageName = page.substring(0, lastDotIndex);
    const action = page.substring(lastDotIndex + 1);

    return pages.some((pages) => pages.page === pageName && pages.actions.includes(action as PageAction));
  }

  // Legacy string format
  return (pages as string[]).includes(page) || permissions?.actions?.includes(page) || false;
};

export const checkModuleAccess = (permissions: AccessiblePages | undefined, module: AccessibleModule): boolean => {
  return permissions?.menuGroups?.includes(module) ?? false;
};

export const checkActionAccess = (modulePermissions: ModulePermissions, module: AccessibleModule, action: PageAction): boolean => {
  return modulePermissions[module]?.includes(action) ?? false;
};

export const getActionsForModule = (modulePermissions: ModulePermissions, module: AccessibleModule): PageAction[] => {
  return modulePermissions[module] ?? [];
};

interface CreateRBACContextValueParams {
  readonly featureName: FeatureName;
  readonly permissions: AccessiblePages;
  readonly isLoading?: boolean;
  readonly setPermissions?: (permissions: AccessiblePages, featureName: FeatureName) => void;
}

export const createRBACContextValue = ({
  featureName,
  permissions,
  isLoading = false,
  setPermissions = () => {},
}: Readonly<CreateRBACContextValueParams>): RBACContextType => {
  const modulePermissions = buildModulePermissions(permissions);

  return {
    featureName,
    permissions,
    hasPageAccess: (page: string) => checkPageAccess(permissions, page),
    hasModuleAccess: (module: AccessibleModule) => checkModuleAccess(permissions, module),
    hasAction: (module: AccessibleModule, action: PageAction) => checkActionAccess(modulePermissions, module, action),
    getModuleActions: (module: AccessibleModule) => getActionsForModule(modulePermissions, module),
    isLoading,
    setPermissions,
  };
};

// ============================================================================
// Sample Data for Storybook Stories
// ============================================================================

// Import authoritative Salesforce-default permissions from a single source
// and re-export so existing consumers continue to work.
// If you need different permission shapes for a specific test, build them
// inline in that test rather than adding ad-hoc exports here.
import { sampleBLPPermissions, sampleSSPermissions, sampleBCOGPermissions } from "@bcc/sampleData/rbac-permissions";

export { sampleBLPPermissions, sampleSSPermissions, sampleBCOGPermissions };

/** Sample RBAC context for BLP users */
export const sampleBLPRBACContext: RBACContextType = createRBACContextValue({
  featureName: "BLP",
  permissions: sampleBLPPermissions,
});

/** Sample RBAC context for SS users */
export const sampleSSRBACContext: RBACContextType = createRBACContextValue({
  featureName: "SS",
  permissions: sampleSSPermissions,
});

/** Sample RBAC context for BC OG users */
export const sampleBCOGRBACContext: RBACContextType = createRBACContextValue({
  featureName: "BC OG",
  permissions: sampleBCOGPermissions,
});

// ============================================================================
// Provider Component
// ============================================================================

interface RBACProviderProps {
  readonly children?: React.ReactNode;
  /** Initial permissions (optional, for testing/SSR) */
  readonly initialPermissions?: AccessiblePages;
  /** Initial feature name (optional, for testing/SSR) */
  readonly initialFeatureName?: FeatureName;
}

const extractPermissionsFromVendor = (vendor: Vendor | undefined): AccessiblePages | undefined => {
  const permissions = vendor?.properties?.permissions;
  if (!permissions) return undefined;

  return {
    pages: permissions.pages,
    menuGroups: permissions.menuGroups,
    ...(permissions.actions ? { actions: permissions.actions } : {}),
  };
};

export function RBACProvider({ children, initialPermissions, initialFeatureName }: Readonly<RBACProviderProps>) {
  const [vendorState] = useVendorContext();
  const [permissions, setPermissions] = useState<AccessiblePages | undefined>(initialPermissions);
  const [featureName, setFeatureName] = useState<FeatureName | undefined>(initialFeatureName);
  const [isLoading, setIsLoading] = useState(!initialPermissions);

  // Hydrate from VendorProvider state, fallback to @vendor localStorage.
  useEffect(() => {
    if (initialPermissions) return;

    const storageVendor = (() => {
      const vendorString = localStorage.getItem("@vendor");
      if (!vendorString) return undefined;
      try {
        return JSON.parse(vendorString) as Vendor;
      } catch {
        return undefined;
      }
    })();

    const vendor = vendorState.currentVendor ?? storageVendor;
    const normalizedPermissions = extractPermissionsFromVendor(vendor);

    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync permissions from vendor state or storage fallback on mount/vendor change
    setPermissions(normalizedPermissions);
    setFeatureName(vendor?.properties?.featureName);

    setIsLoading(false);
  }, [initialPermissions, vendorState.currentVendor]);

  const clearState = useCallback(() => {
    setPermissions(undefined);
    setFeatureName(undefined);
    setIsLoading(false);
  }, []);

  const modulePermissions = useMemo((): ModulePermissions => {
    return buildModulePermissions(permissions);
  }, [permissions]);

  const hasPageAccess = useCallback(
    (page: string): boolean => {
      return checkPageAccess(permissions, page);
    },
    [permissions]
  );

  const hasModuleAccess = useCallback(
    (module: AccessibleModule): boolean => {
      return checkModuleAccess(permissions, module);
    },
    [permissions]
  );

  const hasAction = useCallback(
    (module: AccessibleModule, action: PageAction): boolean => {
      return checkActionAccess(modulePermissions, module, action);
    },
    [modulePermissions]
  );

  const getModuleActions = useCallback(
    (module: AccessibleModule): PageAction[] => {
      return getActionsForModule(modulePermissions, module);
    },
    [modulePermissions]
  );

  const updatePermissions = useCallback((newPermissions: AccessiblePages, newFeatureName: FeatureName) => {
    setPermissions(newPermissions);
    setFeatureName(newFeatureName);
    setIsLoading(false);
  }, []);

  const value: RBACContextType = useMemo(
    () => ({
      featureName,
      permissions,
      hasPageAccess,
      hasModuleAccess,
      hasAction,
      getModuleActions,
      isLoading,
      clearState,
      setPermissions: updatePermissions,
    }),
    [featureName, permissions, hasPageAccess, hasModuleAccess, hasAction, getModuleActions, isLoading, updatePermissions, clearState]
  );

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useRBAC(): RBACContextType {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error("useRBAC must be used within a RBACProvider");
  }
  return context;
}
